import React from "react";
import { ShoppingCart, Info } from "lucide-react";
import { Button } from "../../../../../components";
import {
  AI_LAB_COURSES,
  getPriceForGrade,
  OFFERS_ENABLED
} from "../../../constants/ai-lab-constants";

const CartCard = ({
  childrenData,
  details,
  loading,
  stage,
  handleCheckout,
  isStudent
}) => {
  return (
    <div className="lg:col-span-5 lg:sticky lg:top-24 space-y-4">
      <div className="bg-[#FFF8F0] border border-[#FAD7A0] rounded-3xl overflow-hidden shadow-sm flex flex-col">
        
        {/* Header (Image 1 style) */}
        <div className="bg-[#f39c12] px-5 py-4 flex items-center justify-between text-white shadow-2xs">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            <span className="font-black text-sm sm:text-base tracking-tight">Order Summary</span>
          </div>
          {!isStudent && (
            <span className="bg-white/20 text-white font-extrabold text-[10px] sm:text-xs px-3 py-1 rounded-full shadow-3xs uppercase tracking-wider">
              {childrenData.length} {childrenData.length > 1 ? "Children" : "Child"}
            </span>
          )}
        </div>

        {/* Cart Body */}
        <div className="p-5 space-y-4 text-slate-700 text-xs sm:text-sm">
          
          {/* Per-Child Cost List (Image 2 style) */}
          <div className="space-y-4 pb-4 border-b border-[#FAD7A0]/50">
            {isStudent ? (
              // Student user: Map over the single child's selected courses to render them as separate items
              (() => {
                const child = childrenData[0] || { interests: [], grade: "" };
                const basePrice = getPriceForGrade(child.grade);
                const selectedCourses = child.interests || [];

                if (selectedCourses.length === 0) {
                  return (
                    <div className="flex justify-between items-start border-b border-[#FAD7A0]/30 pb-3 last:border-b-0 last:pb-0">
                      <span className="text-[10px] text-red-400 font-bold italic block">
                        No courses selected
                      </span>
                      <span className="font-bold text-gray-400 text-xs">—</span>
                    </div>
                  );
                }

                return selectedCourses.map((cId) => {
                  const courseLabel = AI_LAB_COURSES.find(c => c.id === cId)?.label || cId;
                  return (
                    <div key={cId} className="flex justify-between items-center border-b border-[#FAD7A0]/30 pb-3 last:border-b-0 last:pb-0">
                      <span className="font-extrabold text-gray-800 text-xs sm:text-sm">
                        {courseLabel}
                      </span>
                      <span className="font-extrabold text-sm text-gray-900">
                        ₹{basePrice.toLocaleString("en-IN")}
                      </span>
                    </div>
                  );
                });
              })()
            ) : (
              // Parent user: Original mapping per child
              childrenData.map((child, idx) => {
                const basePrice = getPriceForGrade(child.grade);
                const selectedCourses = child.interests || [];
                const courseCount = selectedCourses.length;
                const childSubtotal = basePrice * courseCount;

                return (
                  <div key={idx} className="flex justify-between items-start border-b border-[#FAD7A0]/30 pb-3 last:border-b-0 last:pb-0">
                    <div className="space-y-1">
                      <span className="font-extrabold text-gray-800 block text-xs sm:text-sm">
                        Child {idx + 1}: {child.name || `Name`}
                      </span>
                      <span className="text-[10px] text-gray-400 font-bold block uppercase tracking-tight">
                        {child.grade || "No Class Selected"}
                      </span>
                      
                      {/* Course List */}
                      {courseCount > 0 ? (
                        <div className="text-[10px] text-[#E67E22] font-black tracking-tight leading-relaxed">
                          {selectedCourses.map((cId) => AI_LAB_COURSES.find(c => c.id === cId)?.label).join(", ")}
                        </div>
                      ) : (
                        <span className="text-[10px] text-red-400 font-bold italic block">
                          No courses selected
                        </span>
                      )}
                    </div>
                    
                    {/* Price labels */}
                    <div className="text-right">
                      {courseCount > 0 ? (
                        <>
                          <span className="block text-[10px] text-gray-400 font-semibold leading-none mb-1">
                            {courseCount > 1 ? `₹${basePrice} × ${courseCount} (multiple)` : `₹${basePrice}`}
                          </span>
                          <span className="font-extrabold text-sm text-gray-900 block leading-tight">
                            ₹{childSubtotal.toLocaleString("en-IN")}
                          </span>
                        </>
                      ) : (
                        <span className="font-bold text-gray-400 text-xs">—</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Aggregates Summary block (Image 3 style) */}
          <div className="space-y-3 pt-1">
            <div className="flex justify-between items-center text-xs sm:text-sm">
              <span className="font-bold text-slate-500">Total Course Amount</span>
              <span className="font-black text-[#2C3E50] text-sm">
                ₹{details.totalCourseAmount.toLocaleString("en-IN")}
              </span>
            </div>

            <div className="flex justify-between items-center text-xs sm:text-sm">
              <span className="font-bold text-slate-500">Registration Fee</span>
              <span className={`font-black text-sm ${details.registrationFee === 0 ? "text-green-600" : "text-slate-700"}`}>
                ₹{details.registrationFee.toLocaleString("en-IN")}
              </span>
            </div>

            {/* Applied Promotions */}
            {OFFERS_ENABLED && details.totalDiscount > 0 && (
              <div className="p-3 bg-white rounded-2xl flex flex-col gap-2 text-left text-xs text-[#34495E] font-semibold leading-relaxed shadow-3xs" style={{ border: '1.5px solid #22c55e' }}>
                <div className="flex items-center gap-1.5 font-extrabold text-[#2C3E50] mb-0.5 border-b border-gray-100 pb-1.5">
                  <span>🎉</span> Applied Promotions:
                </div>
                {details.childDiscounts > 0 && (
                  <div className="flex justify-between items-center pl-1">
                    <span className="text-[#5D6D7E] font-bold">• Multi-Course Discount</span>
                    <span className="text-green-600 font-extrabold">-₹{details.childDiscounts.toLocaleString("en-IN")}</span>
                  </div>
                )}
                {details.siblingDiscount > 0 && (
                  <div className="flex justify-between items-center pl-1">
                    <span className="text-[#5D6D7E] font-bold">• Sibling Discount</span>
                    <span className="text-green-600 font-extrabold">-₹{details.siblingDiscount.toLocaleString("en-IN")}</span>
                  </div>
                )}
              </div>
            )}

            {/* Total Discount */}
            {OFFERS_ENABLED && details.totalDiscount > 0 && (
              <div className="flex justify-between items-center font-extrabold text-xs sm:text-sm">
                <span className="text-slate-500">Total Discount</span>
                <span className="text-green-600 font-black">-₹{details.totalDiscount.toLocaleString("en-IN")}</span>
              </div>
            )}
          </div>

          {/* Total Amount block (Image 4 style) */}
          <div className="pt-2 border-t border-[#FAD7A0]">
            <div className="flex justify-between items-center">
              <span className="font-black text-sm text-[#2C3E50]">Estimated Total</span>
              <span className="text-xl text-[#E67E22] font-black tracking-tight sm:text-2xl">
                ₹{details.finalTotal.toLocaleString("en-IN")}
              </span>
            </div>
          </div>

          {/* Info text box (Image 5 style) */}
          <div className="p-3 bg-[#FFFBEB] border border-[#FAD7A0] rounded-2xl flex items-start gap-2.5 text-left text-[11px] text-[#D97706] font-bold leading-relaxed shadow-3xs mt-2">
            <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p>
              Classes are live online with experts. We will send class details directly on WhatsApp within 24 hours.
            </p>
          </div>

        </div>
      </div>

      {/* Action button (Image 6 style) */}
      <div className="pt-2">
        <Button
          version="secondary"
          text={loading ? `${stage.replace("_", " ")}...` : `Pay ₹${details.finalTotal.toLocaleString("en-IN")}`}
          handleClick={handleCheckout}
          loading={loading}
          width="100%"
        />
      </div>
    </div>
  );
};

export default CartCard;
